import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import MenuItem from '@mui/material/MenuItem';
import {
  AccountSchema,
  AssetSchema,
  ClientSchema,
  EmployeeSchema,
  ProjectSchema,
  TaxCodeSchema,
  type AccountDto,
  type AssetDto,
  type CampaignDto,
  type ClientDto,
  type EmployeeDto,
  type ProjectDto,
  type SupplierInvoiceDetailLineDto,
} from '@taqon/contracts';
import { useQuery } from '@tanstack/react-query';
import { getData, getPage } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { ActiveClientPicker, ActiveProjectPicker } from '../crm/common';
import { CampaignPicker } from '../marketing/common';
import { AssetPicker } from '../procurement/purchasing';
import { AccountPicker, EmployeePicker, MasterSelect, useActiveMasters } from './lookups';

/**
 * The lines of a supplier document (invoice or credit note): each names its account
 * (FRD-FIN-071) and the dimensions that account's COA flags require (FRD-FIN-020). Cost Center,
 * Employee, Project, Client, Asset and Location come from their real masters; nothing is defaulted
 * (FRD-FIN-022). The Supplier dimension is the document's own supplier.
 */

export interface LineDraft {
  readonly key: number;
  description: string;
  account: AccountDto | null;
  netAmount: string;
  costCenterId: string;
  employee: EmployeeDto | null;
  client: ClientDto | null;
  project: ProjectDto | null;
  asset: AssetDto | null;
  locationId: string;
  /** Credit notes only: the invoice line this line credits (FRD-VAT-037). */
  supplierInvoiceLineId?: string;
  /**
   * Invoices only: the Marketing Campaign the spend belongs to (CLR-18). Optional, a real
   * campaign record, never inferred; it is a reference, not an accounting dimension.
   */
  campaign?: CampaignDto | null;
  /**
   * Invoices only: a tax code chosen in place of the determined one, with the reason
   * (FRD-VAT-019). Offered only to the override authority; the API checks it again.
   */
  overrideTaxCodeId?: string;
  overrideReason?: string;
}

export const emptyLine = (key: number): LineDraft => ({
  key,
  description: '',
  account: null,
  netAmount: '',
  costCenterId: '',
  employee: null,
  client: null,
  project: null,
  asset: null,
  locationId: '',
});

/** What stops the lines being sent, one sentence per problem. */
export function lineProblems(lines: readonly LineDraft[], clientFromHeader = false): string[] {
  return lines.flatMap((line, index) => {
    const n = String(index + 1);
    const out: string[] = [];
    if (line.account?.requires.costCenter === true && line.costCenterId === '') {
      out.push(`Line ${n}: choose a cost centre (required by the account).`);
    }
    if (line.account?.requires.employee === true && line.employee === null) {
      out.push(`Line ${n}: choose an employee (required by the account).`);
    }
    if (line.account?.requires.client === true && line.client === null && !clientFromHeader) {
      out.push(`Line ${n}: choose a client (required by the account).`);
    }
    if (line.account?.requires.project === true && line.project === null) {
      out.push(`Line ${n}: choose a project (required by the account).`);
    }
    if (line.account?.requires.asset === true && line.asset === null) {
      out.push(`Line ${n}: choose an asset (required by the account).`);
    }
    if ((line.overrideTaxCodeId ?? '') !== '' && (line.overrideReason ?? '').trim().length < 5) {
      out.push(`Line ${n}: give the reason for overriding the tax code.`);
    }
    return out;
  });
}

/** Every line has its description, account and amount. */
export function linesComplete(lines: readonly LineDraft[]): boolean {
  return lines.every(
    (line) =>
      line.description.trim() !== '' && line.account !== null && line.netAmount.trim() !== '',
  );
}

/**
 * The lines as the API takes them: only the dimensions chosen are sent. A customer document takes
 * its Client from the header (FRD-FIN-023), so `withoutClient` leaves the line's out.
 */
export function lineRequests(lines: readonly LineDraft[], withoutClient = false) {
  return lines.map((line) => ({
    description: line.description.trim(),
    accountId: line.account?.id ?? '',
    netAmount: line.netAmount.trim(),
    ...(line.costCenterId !== '' ? { costCenterId: line.costCenterId } : {}),
    ...(line.employee !== null ? { employeeId: line.employee.id } : {}),
    ...(line.client !== null && !withoutClient ? { clientId: line.client.id } : {}),
    ...(line.project !== null ? { projectId: line.project.id } : {}),
    ...(line.asset !== null ? { assetId: line.asset.id } : {}),
    ...(line.locationId !== '' ? { locationId: line.locationId } : {}),
    ...(line.supplierInvoiceLineId !== undefined && line.supplierInvoiceLineId !== ''
      ? { supplierInvoiceLineId: line.supplierInvoiceLineId }
      : {}),
    ...(line.campaign !== undefined && line.campaign !== null
      ? { campaignId: line.campaign.id }
      : {}),
    ...(line.overrideTaxCodeId !== undefined && line.overrideTaxCodeId !== ''
      ? {
          taxOverride: {
            taxCodeId: line.overrideTaxCodeId,
            reason: (line.overrideReason ?? '').trim(),
          },
        }
      : {}),
  }));
}

/** Today in the business time zone (SDD §5.1), as a document's default date. */
export function businessToday(timeZone: string | undefined): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: timeZone ?? 'Asia/Dubai' }).format(
    new Date(),
  );
}

export function LinesEditor({
  lines,
  onChange,
  creditableLines,
  withCampaign = false,
  withTaxOverride = false,
  clientFromHeader = false,
}: {
  /** Invoices only: the override authority may override a line's tax code (FRD-VAT-019). */
  withTaxOverride?: boolean | undefined;
  lines: readonly LineDraft[];
  onChange: (lines: LineDraft[]) => void;
  /** Invoices only: each line may name the Marketing Campaign it belongs to (CLR-18). */
  withCampaign?: boolean | undefined;
  /** A customer document: the Client is the header's, so no line asks for one (P2). */
  clientFromHeader?: boolean | undefined;
  /** Credit notes only: the credited invoice's lines, one of which each line may credit. */
  creditableLines?: readonly SupplierInvoiceDetailLineDto[] | undefined;
}) {
  const session = useSession();
  const canSeeCostCentres = hasCapability(session.data, 'organization.cost_center:view');
  const canSeeLocations = hasCapability(session.data, 'organization.location:view');
  const canSeeEmployees = hasCapability(session.data, 'hr.employee:view');
  const canSeeClients = hasCapability(session.data, 'crm.client:view');
  const canSeeProjects = hasCapability(session.data, 'projects.project:view');
  const canSeeAssets = hasCapability(session.data, 'assets.asset:view');
  const canSeeCampaigns = hasCapability(session.data, 'marketing.campaign:view');
  const costCentres = useActiveMasters('/api/v1/organization/cost-centers', canSeeCostCentres);
  const locations = useActiveMasters('/api/v1/organization/locations', canSeeLocations);
  const mayOverride =
    withTaxOverride &&
    hasCapability(session.data, 'tax.vat_transaction_detail:change') &&
    hasCapability(session.data, 'tax.tax_code:view');
  const inputCodes = useQuery({
    queryKey: ['tax', 'codes', 'input-active'],
    enabled: mayOverride,
    queryFn: () =>
      getPage(
        '/api/v1/tax/tax-codes?pageSize=200&filter[direction]=input&filter[status]=active',
        TaxCodeSchema,
      ),
  });

  const update = (key: number, patch: Partial<LineDraft>) => {
    onChange(lines.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };
  const nextKey = lines.reduce((max, line) => Math.max(max, line.key), 0) + 1;

  /**
   * Crediting an invoice line starts from what that line recorded — description, account, amount
   * and dimensions — all of which stay editable (a credit may be partial). The account and
   * employee are read from their masters, so a record the session may not read is left empty.
   */
  const credit = async (key: number, invoiceLineId: string) => {
    const source = creditableLines?.find((l) => l.id === invoiceLineId);
    if (source === undefined) {
      update(key, { supplierInvoiceLineId: '' });
      return;
    }
    const account =
      source.accountCode === null
        ? null
        : ((
            await getPage(
              `/api/v1/ledger/accounts?pageSize=1&sort=code&filter[code]=${encodeURIComponent(source.accountCode)}`,
              AccountSchema,
            ).catch(() => ({ items: [] }))
          ).items[0] ?? null);
    const employee =
      source.employeeId === null || !canSeeEmployees
        ? null
        : await getData(`/api/v1/hr/employees/${source.employeeId}`, EmployeeSchema).catch(
            () => null,
          );
    const client =
      source.clientId === null || !canSeeClients
        ? null
        : await getData(`/api/v1/crm/clients/${source.clientId}`, ClientSchema).catch(() => null);
    const project =
      source.projectId === null || !canSeeProjects
        ? null
        : await getData(`/api/v1/projects/${source.projectId}`, ProjectSchema).catch(() => null);
    const asset =
      source.assetId === null || !canSeeAssets
        ? null
        : await getData(`/api/v1/assets/assets/${source.assetId}`, AssetSchema).catch(() => null);
    update(key, {
      supplierInvoiceLineId: invoiceLineId,
      description: source.description,
      netAmount: source.netAmount,
      account,
      costCenterId: source.costCenterId ?? '',
      employee,
      client,
      project,
      asset,
      locationId: source.locationId ?? '',
    });
  };

  return (
    <>
      {lines.map((line, index) => {
        const n = String(index + 1);
        const needsCostCentre = line.account?.requires.costCenter === true;
        const needsEmployee = line.account?.requires.employee === true;
        const needsClient = line.account?.requires.client === true;
        const needsProject = line.account?.requires.project === true;
        const needsAsset = line.account?.requires.asset === true;
        return (
          <Box
            key={line.key}
            role="group"
            aria-label={`Line ${n}`}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}
          >
            <Stack
              direction="row"
              sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}
            >
              <Typography variant="subtitle2">Line {n}</Typography>
              {lines.length > 1 && (
                <IconButton
                  size="small"
                  aria-label={`Remove line ${n}`}
                  onClick={() => {
                    onChange(lines.filter((l) => l.key !== line.key));
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
            <Stack spacing={2}>
              {creditableLines !== undefined && (
                <TextField
                  select
                  size="small"
                  label="Credits invoice line"
                  value={line.supplierInvoiceLineId ?? ''}
                  helperText="The line's VAT is recorded as an adjustment of that line's VAT."
                  onChange={(e) => {
                    void credit(line.key, e.target.value);
                  }}
                >
                  <MenuItem value="">None (general credit)</MenuItem>
                  {creditableLines.map((source) => (
                    <MenuItem key={source.id} value={source.id}>
                      Line {source.lineNo} — {source.description} — {source.netAmount}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  size="small"
                  label="Line description"
                  required
                  value={line.description}
                  sx={{ flexGrow: 1 }}
                  onChange={(e) => {
                    update(line.key, { description: e.target.value });
                  }}
                />
                <TextField
                  size="small"
                  label="Net amount"
                  required
                  value={line.netAmount}
                  sx={{ width: { md: 160 } }}
                  onChange={(e) => {
                    update(line.key, { netAmount: e.target.value });
                  }}
                />
              </Stack>
              <AccountPicker
                value={line.account}
                onChange={(account) => {
                  update(line.key, { account });
                }}
                helperText={requirementText(line.account)}
              />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <MasterSelect
                  label="Cost centre"
                  value={line.costCenterId}
                  required={needsCostCentre}
                  disabled={!canSeeCostCentres}
                  error={needsCostCentre && line.costCenterId === ''}
                  helperText={
                    canSeeCostCentres ? undefined : 'Needs permission to view cost centres'
                  }
                  options={costCentres.data?.items ?? []}
                  onChange={(value) => {
                    update(line.key, { costCenterId: value });
                  }}
                />
                <Box sx={{ minWidth: { md: 260 }, flexGrow: 1 }}>
                  <EmployeePicker
                    value={line.employee}
                    required={needsEmployee}
                    disabled={!canSeeEmployees}
                    error={needsEmployee && line.employee === null}
                    helperText={canSeeEmployees ? undefined : 'Needs permission to view employees'}
                    onChange={(employee) => {
                      update(line.key, { employee });
                    }}
                  />
                </Box>
                <MasterSelect
                  label="Location"
                  value={line.locationId}
                  disabled={!canSeeLocations}
                  helperText={canSeeLocations ? undefined : 'Needs permission to view locations'}
                  options={locations.data?.items ?? []}
                  onChange={(value) => {
                    update(line.key, { locationId: value });
                  }}
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                {!clientFromHeader && (
                  <Box sx={{ flex: 1 }}>
                    <ActiveClientPicker
                      label="Client"
                      value={line.client}
                      required={needsClient}
                      disabled={!canSeeClients}
                      error={needsClient && line.client === null}
                      helperText={canSeeClients ? undefined : 'Needs permission to view clients'}
                      onChange={(client) => {
                        update(line.key, { client });
                      }}
                    />
                  </Box>
                )}
                <Box sx={{ flex: 1 }}>
                  <ActiveProjectPicker
                    label="Project"
                    value={line.project}
                    required={needsProject}
                    disabled={!canSeeProjects}
                    error={needsProject && line.project === null}
                    helperText={canSeeProjects ? undefined : 'Needs permission to view projects'}
                    onChange={(project) => {
                      update(line.key, { project });
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <AssetPicker
                    value={line.asset}
                    required={needsAsset}
                    disabled={!canSeeAssets}
                    error={needsAsset && line.asset === null}
                    helperText={canSeeAssets ? undefined : 'Needs permission to view assets'}
                    onChange={(asset) => {
                      update(line.key, { asset });
                    }}
                  />
                </Box>
              </Stack>
              {withCampaign &&
                (canSeeCampaigns ? (
                  <CampaignPicker
                    label="Campaign (optional)"
                    value={line.campaign ?? null}
                    helperText="The marketing campaign this spend belongs to, if any"
                    onChange={(campaign) => {
                      update(line.key, { campaign });
                    }}
                  />
                ) : (
                  <TextField
                    size="small"
                    label="Campaign (optional)"
                    disabled
                    value=""
                    helperText="Needs permission to view campaigns"
                  />
                ))}
              {mayOverride && (
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    select
                    size="small"
                    label="Override tax code (optional)"
                    value={line.overrideTaxCodeId ?? ''}
                    sx={{ minWidth: 240 }}
                    helperText="Only where the determined code is wrong for this line"
                    onChange={(e) => {
                      update(line.key, { overrideTaxCodeId: e.target.value });
                    }}
                  >
                    <MenuItem value="">As determined</MenuItem>
                    {(inputCodes.data?.items ?? []).map((code) => (
                      <MenuItem key={code.id} value={code.id}>
                        {code.code} — {code.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  {(line.overrideTaxCodeId ?? '') !== '' && (
                    <TextField
                      size="small"
                      label="Reason for the override"
                      required
                      value={line.overrideReason ?? ''}
                      sx={{ flex: 1 }}
                      onChange={(e) => {
                        update(line.key, { overrideReason: e.target.value });
                      }}
                    />
                  )}
                </Stack>
              )}
            </Stack>
          </Box>
        );
      })}
      <Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => {
            onChange([...lines, emptyLine(nextKey)]);
          }}
        >
          Add line
        </Button>
      </Box>
    </>
  );
}

/** What the chosen account requires, in words, beside the picker. */
export function requirementText(account: AccountDto | null): string | undefined {
  if (account === null) return 'Search by code or name';
  const needs = [
    account.requires.costCenter && 'Cost centre',
    account.requires.employee && 'Employee',
    account.requires.project && 'Project',
    account.requires.client && 'Client',
    account.requires.asset && 'Asset',
  ].filter((x): x is string => typeof x === 'string');
  return needs.length === 0
    ? 'No dimension required beyond the supplier'
    : `Requires: ${needs.join(', ')}`;
}
